import React, { useState } from "react";
import { useQuery, useInfiniteQuery } from "@tanstack/react-query";
import { financeService } from "../../services/api";
import { newRequestId } from "../../utils/requestId";

const input =
  "mt-1 block w-full rounded-md border border-[#dce3dd] bg-white p-2 text-sm";
export const moneyLabel = (amount, currency) => {
  const [whole, fraction] = String(amount).split(".");
  return `${currency} ${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}${fraction !== undefined ? `.${fraction}` : ""}`;
};
const nonzero = (amount) => !/^-?0+(\.0+)?$/.test(String(amount));
const Field = ({ label, children }) => (
  <label className="block text-xs font-semibold text-[#58635d]">
    {label}
    {children}
  </label>
);
function DateField({ label, value, set, max, min, required = true }) {
  return (
    <Field label={label}>
      <input
        className={input}
        required={required}
        type="date"
        value={value}
        max={max}
        min={min}
        onInput={(event) => set(event.target.value)}
        onChange={(event) => set(event.target.value)}
      />
    </Field>
  );
}
function AccountSelect({
  label = "Account",
  value,
  set,
  accounts,
  required = true,
}) {
  return (
    <Field label={label}>
      <select
        required={required}
        className={input}
        value={value || ""}
        onChange={(event) =>
          set(event.target.value ? Number(event.target.value) : null)
        }
      >
        <option value="">Choose an account</option>
        {accounts
          .filter((item) => !item.archived)
          .map((item) => (
            <option key={item.id} value={item.id}>
              {item.name} · {item.currency}
            </option>
          ))}
      </select>
    </Field>
  );
}

function AccountForm({ initial, data, busy, save, close }) {
  const [values, setValues] = useState(
    () =>
      initial || {
        request_id: newRequestId(),
        name: "",
        kind: "bank",
        currency: "",
        opening_balance: "0",
        opened_on: data.today,
        reconcile_every_days: 30,
      },
  );
  const set = (key, value) =>
    setValues((current) => ({ ...current, [key]: value }));
  return (
    <form
      className="surface-panel space-y-4 p-5"
      onSubmit={(event) => {
        event.preventDefault();
        save(values);
      }}
    >
      <h3 className="font-bold">
        {initial ? "Account settings" : "Add a ledger account"}
      </h3>
      <Field label="Account name">
        <input
          required
          maxLength={180}
          className={input}
          value={values.name}
          onChange={(event) => set("name", event.target.value)}
        />
      </Field>
      {!initial && (
        <>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Account type">
              <select
                className={input}
                value={values.kind}
                onChange={(event) => set("kind", event.target.value)}
              >
                {["bank", "cash", "reserve", "card"].map((value) => (
                  <option key={value}>{value}</option>
                ))}
              </select>
            </Field>
            <Field label="Currency">
              <select
                required
                className={input}
                value={values.currency}
                onChange={(event) => set("currency", event.target.value)}
              >
                <option value="">Choose currency</option>
                {Object.keys(data.currencies).map((value) => (
                  <option key={value}>{value}</option>
                ))}
              </select>
            </Field>
          </div>
          <Field label="Opening balance">
            <input
              required
              inputMode="decimal"
              className={input}
              value={values.opening_balance}
              onChange={(event) => set("opening_balance", event.target.value)}
            />
          </Field>
          <DateField
            label="Balance at the start of this date"
            value={values.opened_on}
            max={data.today}
            set={(value) => set("opened_on", value)}
          />
          <p className="text-xs leading-5 text-[#68746d]">
            Use the balance before the entries you will record. For a card,
            enter money owed as a negative balance. Currency and opening balance
            stay fixed; corrections use a recorded adjustment.
          </p>
        </>
      )}
      <Field label="Compare with a statement every (days, optional)">
        <input
          type="number"
          min={1}
          max={365}
          className={input}
          value={values.reconcile_every_days ?? ""}
          onChange={(event) =>
            set(
              "reconcile_every_days",
              event.target.value ? Number(event.target.value) : null,
            )
          }
        />
      </Field>
      {initial && (
        <label className="flex gap-2 text-xs">
          <input
            type="checkbox"
            checked={values.archived}
            onChange={(event) => set("archived", event.target.checked)}
          />
          Archive this account (requires zero balance and no outstanding
          borrowing)
        </label>
      )}
      <div className="flex gap-2">
        <button className="primary-button" disabled={busy}>
          Save account
        </button>
        <button type="button" className="secondary-button" onClick={close}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function EntryForm({ initial, proposal, data, busy, save, close }) {
  const candidate = proposal?.payload;
  const [values, setValues] = useState(() => ({
    request_id: newRequestId(),
    kind: candidate?.kind || "expense",
    account_id: null,
    counter_account_id: null,
    amount: candidate?.amount || "",
    occurred_on: candidate ? candidate.occurred_on || "" : data.today,
    description: candidate?.description || "",
    payee: candidate?.payee || "",
    actor: candidate?.actor || "",
    category: candidate?.category || "",
    source_reference: candidate?.source_reference || "",
    track_repayment: candidate?.track_repayment || false,
    repayment_due_on: candidate?.repayment_due_on || "",
    repayment_of: null,
    ...initial,
  }));
  const set = (key, value) =>
    setValues((current) => ({ ...current, [key]: value }));
  const account = data.accounts.find((item) => item.id === values.account_id);
  const borrows = data.borrowings.filter(
    (item) => !item.reversed && nonzero(item.outstanding),
  );
  return (
    <form
      className="surface-panel space-y-4 p-5"
      onSubmit={(event) => {
        event.preventDefault();
        save({
          ...values,
          counter_account_id:
            values.kind === "transfer" ? values.counter_account_id : null,
          track_repayment:
            values.kind === "transfer" &&
            !values.repayment_of &&
            values.track_repayment,
          repayment_of: values.kind === "transfer" ? values.repayment_of : null,
          repayment_due_on:
            values.kind === "transfer" &&
            values.track_repayment &&
            !values.repayment_of
              ? values.repayment_due_on || null
              : null,
          source_reference: values.source_reference || null,
        });
      }}
    >
      <h3 className="font-bold">
        {proposal
          ? "Review financial entry"
          : "Record money received, spent or moved"}
      </h3>
      {proposal && (
        <div className="rounded-lg bg-[#f4f6f3] p-3 text-sm">
          <blockquote>{proposal.evidence}</blockquote>
          <p className="mt-2 text-xs">
            From {proposal.source_title}. Account hints:{" "}
            {candidate.account_hint || "unknown"}
            {candidate.counter_account_hint
              ? ` → ${candidate.counter_account_hint}`
              : ""}
            . Currency: {candidate.currency || "unknown"}. Choose the accounts
            and verify the amount and date.
          </p>
        </div>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Entry type">
          <select
            className={input}
            value={values.kind}
            onChange={(event) => set("kind", event.target.value)}
          >
            {["income", "expense", "transfer"].map((value) => (
              <option key={value}>{value}</option>
            ))}
          </select>
        </Field>
        <AccountSelect
          label={values.kind === "transfer" ? "From account" : "Account"}
          accounts={data.accounts}
          value={values.account_id}
          set={(value) => set("account_id", value)}
        />
      </div>
      {values.kind === "transfer" && (
        <>
          <AccountSelect
            label="To account"
            accounts={data.accounts.filter(
              (item) => item.id !== values.account_id,
            )}
            value={values.counter_account_id}
            set={(value) => set("counter_account_id", value)}
          />
          <Field label="Repayment of a recorded borrowing (optional)">
            <select
              className={input}
              value={values.repayment_of || ""}
              onChange={(event) => {
                const row = borrows.find(
                  (item) => item.id === Number(event.target.value),
                );
                setValues((current) =>
                  row
                    ? {
                        ...current,
                        repayment_of: row.id,
                        account_id: row.counter_account_id,
                        counter_account_id: row.account_id,
                        amount: row.outstanding,
                        track_repayment: false,
                      }
                    : { ...current, repayment_of: null },
                );
              }}
            >
              <option value="">Regular transfer / new borrowing</option>
              {borrows.map((item) => (
                <option key={item.id} value={item.id}>
                  #{item.id} · {moneyLabel(item.outstanding, item.currency)}{" "}
                  remaining
                </option>
              ))}
            </select>
          </Field>
          {!values.repayment_of && (
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={values.track_repayment}
                onChange={(event) =>
                  set("track_repayment", event.target.checked)
                }
              />
              Track money to return to the source account
            </label>
          )}
          {values.track_repayment && !values.repayment_of && (
            <DateField
              label="Return by (optional)"
              value={values.repayment_due_on}
              min={values.occurred_on}
              required={false}
              set={(value) => set("repayment_due_on", value)}
            />
          )}
        </>
      )}
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={`Amount${account ? ` (${account.currency})` : ""}`}>
          <input
            required
            inputMode="decimal"
            className={input}
            value={values.amount}
            onChange={(event) => set("amount", event.target.value)}
          />
        </Field>
        <DateField
          label="Transaction date"
          value={values.occurred_on}
          min={account?.opened_on}
          max={data.today}
          set={(value) => set("occurred_on", value)}
        />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="Payee / payer (optional)">
          <input
            className={input}
            maxLength={240}
            value={values.payee}
            onChange={(event) => set("payee", event.target.value)}
          />
        </Field>
        <Field label="Cardholder / spent by (optional)">
          <input
            className={input}
            maxLength={180}
            value={values.actor}
            onChange={(event) => set("actor", event.target.value)}
          />
        </Field>
        <Field label="Category (optional)">
          <input
            className={input}
            maxLength={120}
            value={values.category}
            onChange={(event) => set("category", event.target.value)}
          />
        </Field>
        <Field label="Statement transaction reference (optional)">
          <input
            className={input}
            maxLength={240}
            value={values.source_reference}
            onChange={(event) => set("source_reference", event.target.value)}
          />
        </Field>
      </div>
      <Field label="Entry notes">
        <textarea
          className={input}
          maxLength={5000}
          rows={2}
          value={values.description}
          onChange={(event) => set("description", event.target.value)}
        />
      </Field>
      <p className="text-xs text-[#68746d]">
        This records an entry in your personal ledger. Internal transfers update
        both recorded balances and stay separate from income and spending.
      </p>
      <div className="flex gap-2">
        <button className="primary-button" disabled={busy}>
          {proposal ? "Record reviewed entry" : "Record entry"}
        </button>
        <button type="button" className="secondary-button" onClick={close}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function ReconcileForm({ account, data, busy, save, close }) {
  const [values, setValues] = useState(() => ({
    request_id: newRequestId(),
    account_id: account.id,
    as_of: data.today,
    statement_balance: "",
    reference: "",
    notes: "",
  }));
  const set = (key, value) =>
    setValues((current) => ({ ...current, [key]: value }));
  return (
    <form
      className="surface-panel space-y-4 p-5"
      onSubmit={(event) => {
        event.preventDefault();
        save(values);
      }}
    >
      <h3 className="font-bold">Reconcile {account.name}</h3>
      <DateField
        label="Statement as-of date"
        value={values.as_of}
        min={account.opened_on}
        max={data.today}
        set={(value) => set("as_of", value)}
      />
      <Field label={`Statement balance (${account.currency})`}>
        <input
          required
          inputMode="decimal"
          className={input}
          value={values.statement_balance}
          onChange={(event) => set("statement_balance", event.target.value)}
        />
      </Field>
      <Field label="Statement reference">
        <input
          maxLength={240}
          className={input}
          value={values.reference}
          onChange={(event) => set("reference", event.target.value)}
        />
      </Field>
      <Field label="Reconciliation notes">
        <textarea
          maxLength={3000}
          className={input}
          value={values.notes}
          onChange={(event) => set("notes", event.target.value)}
        />
      </Field>
      <p className="text-xs text-[#68746d]">
        The comparison records any difference. Review missing entries before
        recording a separate balance adjustment.
      </p>
      <div className="flex gap-2">
        <button disabled={busy} className="primary-button">
          Compare balances
        </button>
        <button type="button" className="secondary-button" onClick={close}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function CorrectionForm({ correction, today, busy, save, close }) {
  const [values, setValues] = useState(() => ({
    request_id: newRequestId(),
    occurred_on: correction.type === "adjust" ? correction.row.as_of : today,
    reason: "",
  }));
  const row = correction.row;
  return (
    <form
      className="surface-panel space-y-4 p-5"
      onSubmit={(event) => {
        event.preventDefault();
        save(values);
      }}
    >
      <h3 className="font-bold">
        {correction.type === "adjust"
          ? `Record adjustment of ${moneyLabel(row.difference, row.currency)} for ${row.account_name}`
          : `Reverse entry #${row.id}: ${moneyLabel(row.amount, row.currency)}`}
      </h3>
      <p className="text-sm text-[#68746d]">
        {correction.type === "adjust"
          ? "This adds the displayed difference to the ledger after a fresh check that the comparison is unchanged."
          : "The original entry stays in history. A new entry reverses its effect on every affected account."}
      </p>
      {correction.type === "adjust" ? (
        <p className="text-xs">Effective {row.as_of}</p>
      ) : (
        <DateField
          label="Correction date"
          value={values.occurred_on}
          max={today}
          min={row.occurred_on}
          set={(value) =>
            setValues((current) => ({ ...current, occurred_on: value }))
          }
        />
      )}
      <Field label="Reason for correction">
        <textarea
          required
          minLength={4}
          maxLength={3000}
          className={input}
          value={values.reason}
          onChange={(event) =>
            setValues((current) => ({ ...current, reason: event.target.value }))
          }
        />
      </Field>
      <div className="flex gap-2">
        <button disabled={busy} className="primary-button">
          Record correction
        </button>
        <button type="button" className="secondary-button" onClick={close}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function History({ accounts, busy, correct }) {
  const [accountId, setAccountId] = useState(null);
  const history = useInfiniteQuery({
    queryKey: ["ledger-transactions", accountId],
    initialPageParam: null,
    queryFn: ({ pageParam }) =>
      financeService.transactions(accountId, pageParam),
    getNextPageParam: (last) => last.next_cursor ?? undefined,
  });
  const name = (id) =>
    accounts.find((item) => item.id === id)?.name || `Account #${id}`;
  return (
    <section className="space-y-3">
      <h2 className="font-bold">Entry history · all dates</h2>
      <Field label="Filter history by account">
        <select
          className={input}
          value={accountId || ""}
          onChange={(event) =>
            setAccountId(event.target.value ? Number(event.target.value) : null)
          }
        >
          <option value="">All accounts</option>
          {accounts.map((item) => (
            <option key={item.id} value={item.id}>
              {item.name}
            </option>
          ))}
        </select>
      </Field>
      {history.isLoading ? (
        <p role="status">Loading entries…</p>
      ) : history.error ? (
        <p role="alert">
          Could not load entries.{" "}
          <button onClick={() => history.refetch()}>Try again</button>
        </p>
      ) : (
        <>
          {!history.data.pages[0].items.length && (
            <p className="surface-panel p-5 text-sm text-[#68746d]">
              No entries recorded.
            </p>
          )}
          {history.data.pages
            .flatMap((page) => page.items)
            .map((row) => (
              <article key={row.id} className="surface-panel p-4">
                <div className="flex flex-wrap justify-between gap-3">
                  <h3 className="text-sm font-bold">
                    #{row.id} · {row.kind} ·{" "}
                    {moneyLabel(row.amount, row.currency)}
                  </h3>
                  <span className="text-xs">
                    {row.occurred_on}
                    {row.reversed ? " · Reversed" : ""}
                    {row.reverses_id ? ` · Reverses #${row.reverses_id}` : ""}
                  </span>
                </div>
                <p className="mt-2 text-sm">
                  {name(row.account_id)}
                  {row.counter_account_id
                    ? ` → ${name(row.counter_account_id)}`
                    : ""}
                </p>
                <p className="mt-2 text-xs text-[#68746d]">
                  {[row.payee, row.actor, row.category]
                    .filter(Boolean)
                    .join(" · ")}
                </p>
                <p className="mt-2 whitespace-pre-wrap text-sm">
                  {row.description}
                </p>
                {row.source_reference && (
                  <p className="mt-2 text-xs">
                    Reference: {row.source_reference}
                  </p>
                )}
                {!row.reversed && row.kind !== "reversal" && (
                  <button
                    className="quiet-button mt-3 text-xs"
                    disabled={busy}
                    onClick={() => correct({ type: "reverse", row })}
                  >
                    Reverse entry
                  </button>
                )}
              </article>
            ))}
          {history.hasNextPage && (
            <button
              className="secondary-button"
              disabled={history.isFetchingNextPage}
              onClick={() => history.fetchNextPage()}
            >
              Older entries
            </button>
          )}
        </>
      )}
    </section>
  );
}

export default function FinanceDesk({ run, busy, sources = [], openOutbox }) {
  const [period, setPeriod] = useState("");
  const { data, error, isLoading, refetch } = useQuery({
    queryKey: ["finance", period],
    queryFn: () => financeService.overview(period),
    refetchInterval: 60000,
  });
  const [form, setForm] = useState(null);
  const [destination, setDestination] = useState("");
  if (isLoading) return <p role="status">Loading financial records…</p>;
  if (error)
    return (
      <section className="surface-panel p-5">
        <p role="alert">Could not load the ledger.</p>
        <button className="secondary-button mt-3" onClick={() => refetch()}>
          Try again
        </button>
      </section>
    );
  const close = () => setForm(null);
  const names = Object.fromEntries(
    data.accounts.map((item) => [item.id, item.name]),
  );
  const documents = sources.filter((source) =>
    ["docs", "notion"].includes(source.provider),
  );
  return (
    <div className="space-y-5">
      <section className="surface-panel p-5">
        <p className="eyebrow">Know what changed and what needs checking</p>
        <h2 className="mt-2 text-xl font-bold">Financial records</h2>
        <p className="mt-2 text-sm leading-6 text-[#68746d]">
          Balances come from your opening figures and recorded entries. Keep
          reserve borrowing, card spending, repayments and monthly records
          connected.
        </p>
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            className="primary-button"
            onClick={() => setForm({ type: "account" })}
          >
            Add account
          </button>
          <button
            className="secondary-button"
            disabled={!data.accounts.some((item) => !item.archived)}
            onClick={() => setForm({ type: "entry" })}
          >
            Record transaction
          </button>
        </div>
      </section>
      {form?.type === "account" && (
        <AccountForm
          key={form.account?.id || "new"}
          initial={form.account}
          data={data}
          busy={busy}
          close={close}
          save={(values) =>
            run(async () => {
              if (form.account)
                await financeService.editAccount(form.account.id, values);
              else await financeService.createAccount(values);
              close();
            })
          }
        />
      )}
      {form?.type === "entry" && (
        <EntryForm
          key={form.proposal?.id || form.initial?.repayment_of || "new"}
          initial={form.initial}
          proposal={form.proposal}
          data={data}
          busy={busy}
          close={close}
          save={(values) =>
            run(async () => {
              if (form.proposal)
                await financeService.decide(form.proposal.id, {
                  action: "post",
                  transaction: values,
                });
              else await financeService.record(values);
              close();
            })
          }
        />
      )}
      {form?.type === "reconcile" && (
        <ReconcileForm
          key={form.account.id}
          account={form.account}
          data={data}
          busy={busy}
          close={close}
          save={(values) =>
            run(async () => {
              await financeService.reconcile(values);
              close();
            })
          }
        />
      )}
      {["reverse", "adjust"].includes(form?.type) && (
        <CorrectionForm
          key={`${form.type}:${form.row.id}`}
          correction={form}
          today={data.today}
          busy={busy}
          close={close}
          save={(values) =>
            run(async () => {
              if (form.type === "adjust")
                await financeService.adjust(form.row.id, values);
              else await financeService.reverse(form.row.id, values);
              close();
            })
          }
        />
      )}
      <section className="space-y-3">
        <h2 className="font-bold">Recorded balances · {data.today}</h2>
        {!data.accounts.length && (
          <p className="surface-panel p-5 text-sm text-[#68746d]">
            Add an account and its known opening balance to begin.
          </p>
        )}
        <div className="grid gap-3 lg:grid-cols-2">
          {data.accounts.map((item) => (
            <article key={item.id} className="surface-panel p-5">
              <div className="flex justify-between gap-3">
                <h3 className="font-bold">{item.name}</h3>
                <span className="status-pill">
                  {item.archived ? "Archived" : item.kind}
                </span>
              </div>
              <p className="mt-3 text-xl font-bold">
                {moneyLabel(item.balance, item.currency)}
              </p>
              <p className="mt-2 text-xs text-[#68746d]">
                Opening {moneyLabel(item.opening_balance, item.currency)} on{" "}
                {item.opened_on}
              </p>
              <div className="mt-3 flex gap-2">
                {!item.archived && (
                  <button
                    className="secondary-button text-xs"
                    onClick={() =>
                      setForm({ type: "reconcile", account: item })
                    }
                  >
                    Reconcile
                  </button>
                )}
                <button
                  className="quiet-button text-xs"
                  onClick={() => setForm({ type: "account", account: item })}
                >
                  Account settings
                </button>
              </div>
            </article>
          ))}
        </div>
      </section>
      {!!data.proposals.length && (
        <section className="space-y-3">
          <h2 className="font-bold">Financial entries found in your notes</h2>
          <p className="text-xs text-[#68746d]">
            These have not changed your balances. Review each source quote,
            account, amount and date.
          </p>
          {data.proposals.map((proposal) => (
            <article key={proposal.id} className="surface-panel p-5">
              <h3 className="font-bold">
                {proposal.payload.kind} ·{" "}
                {proposal.payload.currency || "Currency unknown"}{" "}
                {proposal.payload.amount}
              </h3>
              <blockquote className="mt-3 border-l-2 border-[#b9d1c2] pl-3 text-sm">
                {proposal.evidence}
              </blockquote>
              <p className="mt-2 text-xs text-[#68746d]">
                {proposal.source_title}
              </p>
              <div className="mt-3 flex gap-2">
                <button
                  className="secondary-button text-xs"
                  disabled={busy}
                  onClick={() => setForm({ type: "entry", proposal })}
                >
                  Review entry
                </button>
                <button
                  className="quiet-button text-xs"
                  disabled={busy}
                  onClick={() =>
                    run(() =>
                      financeService.decide(proposal.id, { action: "dismiss" }),
                    )
                  }
                >
                  Dismiss financial proposal
                </button>
              </div>
            </article>
          ))}
        </section>
      )}
      <section className="space-y-3">
        <h2 className="font-bold">Money to return</h2>
        {!data.borrowings.some((item) => nonzero(item.outstanding)) && (
          <p className="surface-panel p-5 text-sm text-[#68746d]">
            No outstanding borrowing between recorded accounts.
          </p>
        )}
        {data.borrowings
          .filter((item) => nonzero(item.outstanding))
          .map((item) => (
            <article key={item.id} className="surface-panel p-5">
              <h3 className="font-bold">
                {moneyLabel(item.outstanding, item.currency)} back to{" "}
                {names[item.account_id]}
              </h3>
              <p className="mt-2 text-xs">
                Borrowed from {names[item.account_id]} into{" "}
                {names[item.counter_account_id]} on {item.occurred_on} ·{" "}
                {item.repayment_due_on
                  ? `Return by ${item.repayment_due_on}`
                  : "No return date recorded"}
              </p>
              <button
                className="secondary-button mt-3 text-xs"
                onClick={() =>
                  setForm({
                    type: "entry",
                    initial: {
                      kind: "transfer",
                      account_id: item.counter_account_id,
                      counter_account_id: item.account_id,
                      amount: item.outstanding,
                      repayment_of: item.id,
                      description: `Repayment of recorded borrowing #${item.id}`,
                    },
                  })
                }
              >
                Record repayment
              </button>
            </article>
          ))}
      </section>
      <section className="surface-panel space-y-4 p-5">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <h2 className="font-bold">Monthly balances</h2>
          <Field label="Report month">
            <input
              type="month"
              className={input}
              value={period || data.report.period}
              max={data.today.slice(0, 7)}
              onInput={(event) => {
                if (event.target.value) setPeriod(event.target.value);
              }}
              onChange={(event) => {
                if (event.target.value) setPeriod(event.target.value);
              }}
            />
          </Field>
        </div>
        <p className="text-xs text-[#68746d]">
          Through {data.report.through}. Starting values include account
          openings during this month. Currencies stay separate; internal
          transfers are excluded from income and spending.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead>
              <tr>
                {[
                  "Account",
                  "Starting / opening",
                  "Income",
                  "Spending",
                  "Transfers in",
                  "Transfers out",
                  "Adjustments",
                  "Closing",
                ].map((title) => (
                  <th key={title} className="whitespace-nowrap border-b p-2">
                    {title}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.report.accounts.map((row) => (
                <tr key={row.account_id}>
                  <td className="whitespace-nowrap p-2 font-semibold">
                    {row.name} · {row.currency}
                  </td>
                  {[
                    "opening",
                    "income",
                    "expenses",
                    "transfers_in",
                    "transfers_out",
                    "adjustments",
                    "closing",
                  ].map((key) => (
                    <td key={key} className="whitespace-nowrap p-2">
                      {row[key]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {Object.entries(data.report.totals).map(([currency, values]) => (
          <p className="text-sm" key={currency}>
            {currency}: income {values.income}, spending {values.expenses},
            closing balance {values.closing}
          </p>
        ))}
        <Field label="Save monthly summary to">
          <select
            className={input}
            value={destination}
            onChange={(event) => setDestination(event.target.value)}
          >
            <option value="">Choose a connected document or page</option>
            {documents.map((source) => (
              <option key={source.id} value={source.id}>
                {source.title} · {source.provider}
              </option>
            ))}
          </select>
        </Field>
        <button
          className="secondary-button"
          disabled={busy || !destination}
          onClick={() =>
            run(async () => {
              await financeService.draftReport(
                data.report.period,
                Number(destination),
              );
              openOutbox();
            })
          }
        >
          Draft document update
        </button>
        <p className="text-xs text-[#68746d]">
          Review the exact summary in Outbox before appending it to the selected
          document. Add a document in Connections if none is listed.
        </p>
      </section>
      <section className="space-y-3">
        <h2 className="font-bold">Statement comparisons</h2>
        {!data.reconciliations.length && (
          <p className="surface-panel p-5 text-sm text-[#68746d]">
            Use Reconcile on an account to compare against a statement.
          </p>
        )}
        {data.reconciliations.map((row) => (
          <article key={row.id} className="surface-panel p-4">
            <div className="flex justify-between gap-3">
              <h3 className="font-bold">
                {row.account_name} · {row.as_of}
              </h3>
              <span className="status-pill">{row.status}</span>
            </div>
            <p className="mt-2 text-sm">
              Statement {moneyLabel(row.statement_balance, row.currency)} ·
              Recorded now for that date{" "}
              {moneyLabel(row.current_balance, row.currency)}
            </p>
            <p className="mt-2 text-sm">
              Difference {moneyLabel(row.difference, row.currency)}
            </p>
            {row.status === "stale" && (
              <p className="mt-2 text-xs text-[#996617]">
                Entries changed after this comparison. Reconcile again before
                adjusting.
              </p>
            )}
            {row.status === "difference" && (
              <button
                className="secondary-button mt-3 text-xs"
                onClick={() => setForm({ type: "adjust", row })}
              >
                Review balance adjustment
              </button>
            )}
          </article>
        ))}
      </section>
      <History accounts={data.accounts} busy={busy} correct={setForm} />
    </div>
  );
}
